<script setup>
import { ref, computed, reactive, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, helpers } from '@vuelidate/validators';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import apiToolsAPI from 'dashboard/api/captain/apiTools';

const props = defineProps({
  tool: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();
const store = useStore();

const assistants = useMapGetter('captainAssistants/getRecords');
const uiFlags = useMapGetter('captainApiTools/getUIFlags');

const isSubmitting = computed(
  () => uiFlags.value.isCreating || uiFlags.value.isUpdating
);
const isTesting = ref(false);
const testResult = ref(null);

const form = reactive({
  name: '',
  title: '',
  description: '',
  assistant_id: null,
  icon: '',
  enabled: true,
  api_spec: {
    method: 'GET',
    url: '',
    auth: {
      type: 'none',
    },
    parameters: {
      type: 'object',
      properties: {},
      required: [],
    },
  },
});

// Initialize form if editing
onMounted(() => {
  if (props.tool) {
    form.name = props.tool.name || '';
    form.title = props.tool.title || '';
    form.description = props.tool.description || '';
    form.assistant_id = props.tool.assistant_id || null;
    form.icon = props.tool.icon || '';
    form.enabled = props.tool.enabled !== false;
    form.api_spec = {
      ...form.api_spec,
      ...props.tool.api_spec,
    };
  }

  if (!assistants.value.length) {
    store.dispatch('captainAssistants/get');
  }
});

const parametersJson = ref('');

// Initialize parameters JSON
watch(
  () => form.api_spec.parameters,
  newVal => {
    if (newVal && typeof newVal === 'object') {
      parametersJson.value = JSON.stringify(newVal, null, 2);
    }
  },
  { immediate: true }
);

// Update parameters when JSON changes
watch(parametersJson, newVal => {
  try {
    form.api_spec.parameters = JSON.parse(newVal);
  } catch (e) {
    // Invalid JSON, don't update
  }
});

const httpMethods = [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'PATCH', label: 'PATCH' },
  { value: 'DELETE', label: 'DELETE' },
];

const authTypes = [
  { value: 'none', label: t('CAPTAIN.TOOLS.AUTH_TYPES.NONE') },
  { value: 'bearer', label: t('CAPTAIN.TOOLS.AUTH_TYPES.BEARER') },
  { value: 'api_key', label: t('CAPTAIN.TOOLS.AUTH_TYPES.API_KEY') },
  { value: 'basic', label: t('CAPTAIN.TOOLS.AUTH_TYPES.BASIC') },
];

const assistantOptions = computed(() =>
  assistants.value.map(assistant => ({
    value: assistant.id,
    label: assistant.name,
  }))
);

const validationRules = {
  name: {
    required: helpers.withMessage(
      t('CAPTAIN.TOOLS.VALIDATIONS.NAME_REQUIRED'),
      required
    ),
    minLength: helpers.withMessage(
      t('CAPTAIN.TOOLS.VALIDATIONS.NAME_MIN_LENGTH'),
      minLength(2)
    ),
  },
  title: {
    required: helpers.withMessage(
      t('CAPTAIN.TOOLS.VALIDATIONS.TITLE_REQUIRED'),
      required
    ),
  },
  description: {
    required: helpers.withMessage(
      t('CAPTAIN.TOOLS.VALIDATIONS.DESCRIPTION_REQUIRED'),
      required
    ),
    minLength: helpers.withMessage(
      t('CAPTAIN.TOOLS.VALIDATIONS.DESCRIPTION_MIN_LENGTH'),
      minLength(20)
    ),
  },
  assistant_id: {
    required: helpers.withMessage(
      t('CAPTAIN.TOOLS.VALIDATIONS.ASSISTANT_REQUIRED'),
      required
    ),
  },
};

const v$ = useVuelidate(validationRules, form);

const getErrorMessage = field => {
  return v$.value[field].$error ? v$.value[field].$errors[0].$message : '';
};

const handleAuthTypeChange = () => {
  // Reset auth fields when type changes
  form.api_spec.auth = { type: form.api_spec.auth.type };
};

const handleTest = async () => {
  try {
    isTesting.value = true;
    testResult.value = null;

    const { data } = await apiToolsAPI.test({
      api_tool: form,
    });

    testResult.value = {
      success: true,
      result: data.result,
    };
    useAlert(t('CAPTAIN.TOOLS.TEST.SUCCESS'));
  } catch (error) {
    testResult.value = {
      success: false,
      error: error.response?.data?.error || error.message,
    };
    useAlert(t('CAPTAIN.TOOLS.TEST.FAILED'));
  } finally {
    isTesting.value = false;
  }
};

const handleSubmit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  // Validate JSON parameters
  try {
    JSON.parse(parametersJson.value);
  } catch (e) {
    useAlert(t('CAPTAIN.TOOLS.VALIDATIONS.INVALID_JSON'));
    return;
  }

  emit('submit', form);
};

const handleCancel = () => {
  emit('cancel');
};
</script>

<template>
  <form class="flex flex-col gap-6" @submit.prevent="handleSubmit">
    <!-- Basic Information Section -->
    <div class="flex flex-col gap-4">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('CAPTAIN.TOOLS.SECTIONS.BASIC_INFO') }}
      </h3>

      <div class="flex flex-col gap-1">
        <label
          for="assistant"
          class="mb-0.5 text-sm font-medium text-n-slate-12"
        >
          {{ $t('CAPTAIN.TOOLS.FORM.ASSISTANT.LABEL') }}
        </label>
        <ComboBox
          id="assistant"
          v-model="form.assistant_id"
          :options="assistantOptions"
          :has-error="!!getErrorMessage('assistant_id')"
          :placeholder="$t('CAPTAIN.TOOLS.FORM.ASSISTANT.PLACEHOLDER')"
          class="[&>div>button]:bg-n-alpha-black2"
          :message="getErrorMessage('assistant_id')"
        />
      </div>

      <Input
        v-model="form.title"
        :label="$t('CAPTAIN.TOOLS.FORM.TITLE.LABEL')"
        :placeholder="$t('CAPTAIN.TOOLS.FORM.TITLE.PLACEHOLDER')"
        :message="getErrorMessage('title')"
        :message-type="getErrorMessage('title') ? 'error' : 'info'"
      />

      <Input
        v-model="form.name"
        :label="$t('CAPTAIN.TOOLS.FORM.NAME.LABEL')"
        :placeholder="$t('CAPTAIN.TOOLS.FORM.NAME.PLACEHOLDER')"
        :message="getErrorMessage('name') || $t('CAPTAIN.TOOLS.FORM.NAME.HELP')"
        :message-type="getErrorMessage('name') ? 'error' : 'info'"
      />

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('CAPTAIN.TOOLS.FORM.DESCRIPTION.LABEL') }}
          <span class="text-xs text-n-slate-11 font-normal ml-1">
            {{ $t('CAPTAIN.TOOLS.FORM.DESCRIPTION.SUBTITLE') }}
          </span>
        </label>
        <textarea
          v-model="form.description"
          :placeholder="$t('CAPTAIN.TOOLS.FORM.DESCRIPTION.PLACEHOLDER')"
          class="block w-full reset-base text-sm px-3 py-2.5 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out resize-y min-h-[100px]"
          :class="[
            getErrorMessage('description')
              ? 'outline-n-ruby-8 dark:outline-n-ruby-8 hover:outline-n-ruby-9 dark:hover:outline-n-ruby-9'
              : 'outline-n-weak dark:outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus:outline-n-brand dark:focus:outline-n-brand',
          ]"
          rows="4"
        />
        <p
          class="text-xs"
          :class="
            getErrorMessage('description') ? 'text-n-ruby-9' : 'text-n-slate-11'
          "
        >
          {{
            getErrorMessage('description') ||
            $t('CAPTAIN.TOOLS.FORM.DESCRIPTION.HELP')
          }}
        </p>
      </div>
    </div>

    <!-- API Configuration Section -->
    <div class="flex flex-col gap-4 pt-4 border-t border-n-weak">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('CAPTAIN.TOOLS.SECTIONS.API_CONFIG') }}
      </h3>

      <div class="flex flex-col gap-1">
        <label for="method" class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ $t('CAPTAIN.TOOLS.FORM.METHOD.LABEL') }}
        </label>
        <ComboBox
          id="method"
          v-model="form.api_spec.method"
          :options="httpMethods"
          class="[&>div>button]:bg-n-alpha-black2"
        />
      </div>

      <Input
        v-model="form.api_spec.url"
        :label="$t('CAPTAIN.TOOLS.FORM.URL.LABEL')"
        :placeholder="$t('CAPTAIN.TOOLS.FORM.URL.PLACEHOLDER')"
        :message="$t('CAPTAIN.TOOLS.FORM.URL.HELP')"
        message-type="info"
      />
    </div>

    <!-- Authentication Section -->
    <div class="flex flex-col gap-4 pt-4 border-t border-n-weak">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('CAPTAIN.TOOLS.SECTIONS.AUTHENTICATION') }}
      </h3>

      <div class="flex flex-col gap-1">
        <label
          for="authType"
          class="mb-0.5 text-sm font-medium text-n-slate-12"
        >
          {{ $t('CAPTAIN.TOOLS.FORM.AUTH_TYPE.LABEL') }}
        </label>
        <ComboBox
          id="authType"
          v-model="form.api_spec.auth.type"
          :options="authTypes"
          class="[&>div>button]:bg-n-alpha-black2"
          @update:model-value="handleAuthTypeChange"
        />
      </div>

      <template v-if="form.api_spec.auth.type === 'bearer'">
        <Input
          v-model="form.api_spec.auth.token"
          :label="$t('CAPTAIN.TOOLS.FORM.AUTH_TOKEN.LABEL')"
          :placeholder="$t('CAPTAIN.TOOLS.FORM.AUTH_TOKEN.PLACEHOLDER')"
          type="password"
        />
      </template>

      <template v-if="form.api_spec.auth.type === 'api_key'">
        <Input
          v-model="form.api_spec.auth.key"
          :label="$t('CAPTAIN.TOOLS.FORM.AUTH_KEY_NAME.LABEL')"
          :placeholder="$t('CAPTAIN.TOOLS.FORM.AUTH_KEY_NAME.PLACEHOLDER')"
        />
        <Input
          v-model="form.api_spec.auth.token"
          :label="$t('CAPTAIN.TOOLS.FORM.AUTH_KEY_VALUE.LABEL')"
          :placeholder="$t('CAPTAIN.TOOLS.FORM.AUTH_KEY_VALUE.PLACEHOLDER')"
          type="password"
        />
      </template>

      <template v-if="form.api_spec.auth.type === 'basic'">
        <Input
          v-model="form.api_spec.auth.username"
          :label="$t('CAPTAIN.TOOLS.FORM.AUTH_USERNAME.LABEL')"
          :placeholder="$t('CAPTAIN.TOOLS.FORM.AUTH_USERNAME.PLACEHOLDER')"
        />
        <Input
          v-model="form.api_spec.auth.password"
          :label="$t('CAPTAIN.TOOLS.FORM.AUTH_PASSWORD.LABEL')"
          :placeholder="$t('CAPTAIN.TOOLS.FORM.AUTH_PASSWORD.PLACEHOLDER')"
          type="password"
        />
      </template>
    </div>

    <!-- Parameters Section -->
    <div class="flex flex-col gap-4 pt-4 border-t border-n-weak">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('CAPTAIN.TOOLS.SECTIONS.PARAMETERS') }}
      </h3>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('CAPTAIN.TOOLS.FORM.PARAMETERS.LABEL') }}
        </label>
        <textarea
          v-model="parametersJson"
          class="block w-full reset-base text-sm px-3 py-2.5 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 text-n-slate-12 transition-all duration-500 ease-in-out resize-y min-h-[200px] font-mono outline-n-weak dark:outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus:outline-n-brand dark:focus:outline-n-brand"
          placeholder='{"type":"object","properties":{},"required":[]}'
        />
        <p class="text-xs text-n-slate-11">
          {{ $t('CAPTAIN.TOOLS.FORM.PARAMETERS.HELP') }}
        </p>
      </div>
    </div>

    <!-- Test Results -->
    <div v-if="testResult" class="pt-4 border-t border-n-weak">
      <div
        class="p-4 rounded-lg"
        :class="[
          testResult.success
            ? 'bg-green-50 dark:bg-green-900/20'
            : 'bg-red-50 dark:bg-red-900/20',
        ]"
      >
        <h4
          class="text-sm font-medium mb-2"
          :class="
            testResult.success
              ? 'text-green-900 dark:text-green-100'
              : 'text-red-900 dark:text-red-100'
          "
        >
          {{
            testResult.success
              ? $t('CAPTAIN.TOOLS.TEST.SUCCESS')
              : $t('CAPTAIN.TOOLS.TEST.FAILED')
          }}
        </h4>
        <pre
          class="text-xs overflow-auto max-h-48"
          :class="
            testResult.success
              ? 'text-green-800 dark:text-green-200'
              : 'text-red-800 dark:text-red-200'
          "
          >{{
            testResult.success
              ? JSON.stringify(testResult.result, null, 2)
              : testResult.error
          }}</pre>
      </div>
    </div>

    <!-- Form Actions -->
    <div
      class="flex items-center justify-between gap-3 pt-4 border-t border-n-weak"
    >
      <Button
        type="button"
        variant="hollow"
        color="slate"
        :label="$t('CAPTAIN.TOOLS.FORM.TEST_API')"
        :is-loading="isTesting"
        :disabled="isTesting || isSubmitting"
        @click="handleTest"
      />
      <div class="flex gap-3">
        <Button
          type="button"
          variant="hollow"
          color="slate"
          :label="$t('CAPTAIN.TOOLS.FORM.CANCEL')"
          :disabled="isSubmitting"
          @click="handleCancel"
        />
        <Button
          type="submit"
          :label="
            tool
              ? $t('CAPTAIN.TOOLS.FORM.UPDATE')
              : $t('CAPTAIN.TOOLS.FORM.CREATE')
          "
          :is-loading="isSubmitting"
          :disabled="isSubmitting"
        />
      </div>
    </div>
  </form>
</template>
